import { Menu } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "react-router-dom";
import { currentOrgSlug, homePath } from "./currentOrg";
import { OrgSwitcher } from "./OrgSwitcher";
import { ShellProvider, useShellCrumbs } from "./shellContext";
import { SidebarNav } from "./SidebarNav";
import { UserMenu } from "./UserMenu";
import { useMe } from "@/api/hooks/auth";
import logoBlack from "@/assets/codemonk-logo-long-black.svg";
import logoWhite from "@/assets/codemonk-logo-long-white.svg";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="mt-auto border-t border-border">
      <div className="mx-auto flex max-w-content flex-wrap items-center gap-4 px-4 py-6 text-sm text-fg-muted sm:px-6 lg:px-8">
        <span>{t("common.footer.copyright")}</span>
        <nav aria-label={t("common.footer.legal")} className="ml-auto flex gap-4">
          <Link to="/privacy" className="text-fg-muted underline">
            {t("common.footer.privacy")}
          </Link>
          <Link to="/terms" className="text-fg-muted underline">
            {t("common.footer.terms")}
          </Link>
        </nav>
      </div>
    </footer>
  );
}

function ShellFrame() {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const crumbs = useShellCrumbs();
  const [menuOpen, setMenuOpen] = useState(false);
  const org = currentOrgSlug(location.pathname, me.data);
  const switcher = me.data && !me.data.is_staff ? <OrgSwitcher memberships={me.data.memberships} current={org} /> : null;

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-shell">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-toast focus:rounded-pill focus:bg-surface focus:px-4 focus:py-2"
      >
        {t("common.skipToContent")}
      </a>

      <aside className="hidden bg-surface-inverse lg:block">
        <div className="sticky top-0 flex h-screen flex-col gap-6 overflow-y-auto px-4 py-6">
          <Link to={homePath(me.data)} className="flex items-center px-3" aria-label={t("common.appName")}>
            <img src={logoWhite} alt={t("common.logoAlt")} width={144} height={20} className="h-5 w-auto" />
          </Link>
          <SidebarNav org={org} />
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-col">
        <header className="sticky top-0 z-header border-b border-border bg-surface">
          <div className="flex min-h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t("shell.openMenu")}>
                  <Menu aria-hidden="true" className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent tone="inverse" className="px-4">
                <SheetTitle className="px-3 pr-14">
                  <img src={logoWhite} alt={t("common.logoAlt")} width={144} height={20} className="h-5 w-auto" />
                </SheetTitle>
                <SheetDescription className="sr-only">{t("shell.menuDescription")}</SheetDescription>
                <SidebarNav org={org} onNavigate={() => setMenuOpen(false)} />
              </SheetContent>
            </Sheet>
            <Link to={homePath(me.data)} className="flex items-center lg:hidden" aria-label={t("common.appName")}>
              <img src={logoBlack} alt="" width={144} height={20} className="h-5 w-auto" />
            </Link>
            <Breadcrumb label={t("shell.breadcrumb")} items={crumbs} className="hidden flex-1 lg:block" />
            <div className="ml-auto flex min-w-0 items-center gap-1">
              <div className="hidden min-w-0 sm:block">{switcher}</div>
              <UserMenu />
            </div>
          </div>
          {crumbs.length > 0 ? (
            <div className="border-t border-border px-4 py-2 sm:px-6 lg:hidden">
              <Breadcrumb label={t("shell.breadcrumbMobile")} items={crumbs} />
            </div>
          ) : null}
        </header>
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-content flex-1 px-4 py-8 focus:outline-none sm:px-6 lg:px-8">
          {switcher ? <div className="-mt-4 mb-4 sm:hidden">{switcher}</div> : null}
          <Outlet />
        </main>
        <Footer />
      </div>
    </div>
  );
}

/** Signed-in layout: sidebar (desktop) or menu sheet (phone/tablet), breadcrumb, org and user menus. */
export function AuthedShell() {
  return (
    <ShellProvider>
      <ShellFrame />
    </ShellProvider>
  );
}
